import type { CharacterInfo } from '@gofranz/gpt-muse-common';
import { Button, Checkbox, Grid, Group, Modal, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconCopy,
  IconDownload,
  IconFileTypePdf,
  IconPhoto,
  IconShare,
} from '@tabler/icons-react';
import { jsPDF } from 'jspdf';
import React, { useCallback, useState } from 'react';
import type { ChatMessage } from '../../lib/chat-db';

interface SaveChatModalProps {
  opened: boolean;
  onClose: () => void;
  messages: ChatMessage[];
  conversationTitle?: string;
  selectedCharacter?: string | null;
  characters?: CharacterInfo[];
}

export const SaveChatModal = React.memo<SaveChatModalProps>(
  ({
    opened,
    onClose,
    messages,
    conversationTitle = 'Chat',
    selectedCharacter,
    characters = [],
  }) => {
    // Check if Web Share API is available
    const canShare = typeof navigator !== 'undefined' && 'share' in navigator;

    // Check if any messages contain images
    const hasImages = messages.some((msg) => msg.images && msg.images.length > 0);

    // State for including images in export
    const [includeImages, setIncludeImages] = useState(true);
    const getAssistantLabel = useCallback((): string => {
      if (!selectedCharacter) {
        return 'Assistant';
      }
      if (selectedCharacter === 'custom') {
        return 'Assistant (Custom)';
      }
      const character = characters.find((c) => c.id === selectedCharacter);
      return character ? character.name : 'Assistant';
    }, [selectedCharacter, characters]);

    const formatAsPlaintext = useCallback(
      (messages: ChatMessage[], includeImages: boolean = true): string => {
        return messages
          .map((msg) => {
            const roleLabel = msg.role === 'user' ? 'USER' : getAssistantLabel().toUpperCase();
            let content = `${roleLabel}: ${msg.content}`;
            if (msg.reasoning) {
              content += `\n\nREASONING: ${msg.reasoning}`;
            }
            if (includeImages && msg.images && msg.images.length > 0) {
              content += `\n\nIMAGES: ${msg.images.length} image(s) included`;
            }
            return content;
          })
          .join('\n\n---\n\n');
      },
      [getAssistantLabel]
    );

    const formatAsMarkdown = useCallback(
      (messages: ChatMessage[], includeImages: boolean = true): string => {
        let markdown = `# ${conversationTitle}\n\n`;

        messages.forEach((msg, index) => {
          const roleHeader = msg.role === 'user' ? '## 🤔 User' : `## 🤖 ${getAssistantLabel()}`;
          markdown += `${roleHeader}\n\n${msg.content}\n\n`;

          // Add images to markdown
          if (includeImages && msg.images && msg.images.length > 0) {
            msg.images.forEach((image, imgIndex) => {
              markdown += `![Generated Image ${imgIndex + 1}](${image.url})\n\n`;
            });
          }

          if (msg.reasoning) {
            markdown += `### 💭 Reasoning\n\n${msg.reasoning}\n\n`;
          }

          if (index < messages.length - 1) {
            markdown += '---\n\n';
          }
        });

        return markdown;
      },
      [conversationTitle, getAssistantLabel]
    );

    const formatAsJSON = useCallback(
      (messages: ChatMessage[], includeImages: boolean = true): string => {
        const exportData = {
          conversationTitle,
          selectedCharacter,
          exportedAt: new Date().toISOString(),
          messageCount: messages.length,
          messages: messages.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            reasoning: msg.reasoning,
            images: includeImages ? msg.images : undefined,
            model: msg.model,
            timestamp: msg.timestamp,
          })),
        };

        return JSON.stringify(exportData, null, 2);
      },
      [conversationTitle, selectedCharacter]
    );

    const downloadFile = useCallback(
      (content: string, filename: string, mimeType: string = 'text/plain') => {
        const element = document.createElement('a');
        const file = new Blob([content], { type: mimeType });
        element.href = URL.createObjectURL(file);
        element.download = filename;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        notifications.show({
          title: 'Download Started',
          message: `${filename} is being downloaded`,
          color: 'green',
          icon: <IconCheck size={16} />,
          autoClose: 3000,
        });
      },
      []
    );

    const generatePDF = useCallback(
      async (messages: ChatMessage[], includeImages: boolean = true) => {
        try {
          const pdf = new jsPDF();
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const margin = 20;
          const contentWidth = pageWidth - 2 * margin;
          const bottomMargin = 30; // Leave space at bottom for page numbers
          const lineHeight = 5;
          let yPosition = margin;

          // Helper function to check if we need a new page and add one if needed
          const checkNewPage = (requiredSpace: number = 20) => {
            if (yPosition + requiredSpace > pageHeight - bottomMargin) {
              pdf.addPage();
              yPosition = margin;
              return true;
            }
            return false;
          };

          // Helper function to add text with automatic page breaks (simplified, no bold for now)
          const addFormattedTextWithPageBreaks = (
            text: string,
            x: number,
            startY: number,
            fontSize: number,
            maxWidth: number,
            textColor: number[] = [0, 0, 0]
          ) => {
            pdf.setFontSize(fontSize);
            pdf.setTextColor(textColor[0], textColor[1], textColor[2]);

            // Clean up text to handle special characters better
            const cleanText = text
              .replace(/→/g, '->') // Replace arrows with ASCII equivalent
              .replace(/"/g, '"') // Replace smart quotes
              .replace(/"/g, '"')
              .replace(/'/g, "'") // Replace smart apostrophes
              .replace(/'/g, "'")
              .replace(/—/g, '--') // Replace em dashes
              .replace(/–/g, '-') // Replace en dashes
              .replace(/\*\*([^*]+)\*\*/g, '$1'); // Remove bold markers for now

            const lines = pdf.splitTextToSize(cleanText, maxWidth);
            let currentY = startY;

            for (let i = 0; i < lines.length; i++) {
              // Check if we need a new page for this line
              if (currentY + lineHeight > pageHeight - bottomMargin) {
                pdf.addPage();
                currentY = margin;
              }

              pdf.text(lines[i], x, currentY);
              currentY += lineHeight;
            }

            return currentY;
          };

          // Add title
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.text(conversationTitle, margin, yPosition);
          yPosition += 20;

          // Add export date and website
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(128, 128, 128);
          pdf.text(`Exported on ${new Date().toLocaleString()}`, margin, yPosition);
          yPosition += 8;
          pdf.text('Generated on https://gpt-muse.com', margin, yPosition);
          yPosition += 15;

          // Reset text color
          pdf.setTextColor(0, 0, 0);

          for (const [index, message] of messages.entries()) {
            // Estimate space needed for this message (header + content preview)
            const estimatedSpace = 50; // Conservative estimate
            checkNewPage(estimatedSpace);

            // Add role header
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            const roleLabel = message.role === 'user' ? 'USER' : getAssistantLabel().toUpperCase();
            pdf.text(roleLabel, margin, yPosition);
            yPosition += 6;

            // Add timestamp
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(128, 128, 128);
            pdf.text(message.timestamp.toLocaleString(), margin, yPosition);
            yPosition += 10;

            // Reset text color and add separator line
            pdf.setTextColor(0, 0, 0);
            pdf.line(margin, yPosition, pageWidth - margin, yPosition);
            yPosition += 8;

            // Handle reasoning first (if present) - DeepSeek R1 shows reasoning before response
            if (message.reasoning) {
              checkNewPage(20); // Ensure we have space for reasoning header

              pdf.setFontSize(9);
              pdf.setTextColor(128, 128, 128);
              pdf.text('Reasoning:', margin, yPosition);
              yPosition += 6;

              yPosition = addFormattedTextWithPageBreaks(
                message.reasoning,
                margin,
                yPosition,
                9,
                contentWidth,
                [128, 128, 128]
              );
              yPosition += 8;

              pdf.setTextColor(0, 0, 0);
            }

            // Add message content with automatic page breaks
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            yPosition = addFormattedTextWithPageBreaks(
              message.content,
              margin,
              yPosition,
              10,
              contentWidth
            );
            yPosition += 8;

            // Handle images
            if (includeImages && message.images && message.images.length > 0) {
              for (const [, image] of message.images.entries()) {
                try {
                  // Add spacing before image
                  yPosition += 5;

                  // Check if we need a new page for the image (reserve 100px)
                  checkNewPage(100);

                  // Add the image with better sizing
                  const imgWidth = Math.min(contentWidth, 120);

                  // Try to add the image
                  pdf.addImage(image.url, 'PNG', margin, yPosition, imgWidth, 0); // Auto height
                  yPosition += 85; // Fixed spacing to prevent overlap

                  // Add spacing after each image
                  yPosition += 10;
                } catch (error) {
                  console.warn('Failed to add image to PDF:', error);
                  // Add text fallback
                  pdf.setFontSize(9);
                  pdf.setTextColor(128, 128, 128);
                  pdf.text(`[Image failed to load]`, margin, yPosition);
                  yPosition += 10;
                  pdf.setTextColor(0, 0, 0);
                }
              }
            }

            // Add spacing between messages
            yPosition += 10;

            // Add separator line between messages (except for the last one)
            if (index < messages.length - 1) {
              checkNewPage(20); // Ensure we have space for separator

              pdf.setDrawColor(200, 200, 200);
              pdf.line(margin, yPosition, pageWidth - margin, yPosition);
              pdf.setDrawColor(0, 0, 0);
              yPosition += 15;
            }
          }

          // Add page numbers
          const pageCount = pdf.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            pdf.setFontSize(8);
            pdf.setTextColor(128, 128, 128);
            pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 30, pageHeight - 10);
          }

          // Save the PDF
          const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
          pdf.save(`chat-${timestamp}.pdf`);

          notifications.show({
            title: 'PDF Generated',
            message: 'Chat exported as PDF successfully',
            color: 'green',
            icon: <IconCheck size={16} />,
            autoClose: 3000,
          });
        } catch (error) {
          console.error('Failed to generate PDF:', error);
          notifications.show({
            title: 'PDF Generation Failed',
            message: 'Failed to generate PDF. Please try another format.',
            color: 'red',
            autoClose: 5000,
          });
        }
      },
      [conversationTitle, getAssistantLabel]
    );

    const copyToClipboard = useCallback(async (content: string, format: string) => {
      try {
        await navigator.clipboard.writeText(content);
        notifications.show({
          title: 'Copied to Clipboard',
          message: `Chat exported as ${format} and copied to clipboard`,
          color: 'green',
          icon: <IconCheck size={16} />,
          autoClose: 3000,
        });
      } catch (error) {
        notifications.show({
          title: 'Copy Failed',
          message: 'Failed to copy to clipboard. Please try the download option instead.',
          color: 'red',
          autoClose: 5000,
        });
      }
    }, []);

    const shareContent = useCallback(
      async (format: 'plaintext' | 'markdown' | 'json' | 'pdf') => {
        if (!canShare) {
          notifications.show({
            title: 'Sharing Not Available',
            message: 'Web sharing is not supported on this browser. Use Copy or Download instead.',
            color: 'orange',
            autoClose: 3000,
          });
          return;
        }

        if (messages.length === 0) {
          notifications.show({
            title: 'No Messages',
            message: 'There are no messages to share',
            color: 'orange',
            autoClose: 3000,
          });
          return;
        }

        try {
          if (format === 'pdf') {
            // For PDF, we need to generate it as a blob and share as file
            const pdf = new jsPDF();
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 20;
            const contentWidth = pageWidth - 2 * margin;
            const bottomMargin = 30;
            const lineHeight = 5;
            let yPosition = margin;

            // Helper function to check if we need a new page and add one if needed
            const checkNewPage = (requiredSpace: number = 20) => {
              if (yPosition + requiredSpace > pageHeight - bottomMargin) {
                pdf.addPage();
                yPosition = margin;
                return true;
              }
              return false;
            };

            // Helper function to add text with automatic page breaks
            const addFormattedTextWithPageBreaks = (
              text: string,
              x: number,
              startY: number,
              fontSize: number,
              maxWidth: number,
              textColor: number[] = [0, 0, 0]
            ) => {
              pdf.setFontSize(fontSize);
              pdf.setTextColor(textColor[0], textColor[1], textColor[2]);

              const cleanText = text
                .replace(/→/g, '->')
                .replace(/"/g, '"')
                .replace(/"/g, '"')
                .replace(/'/g, "'")
                .replace(/'/g, "'")
                .replace(/—/g, '--')
                .replace(/–/g, '-')
                .replace(/\*\*([^*]+)\*\*/g, '$1');

              const lines = pdf.splitTextToSize(cleanText, maxWidth);
              let currentY = startY;

              for (let i = 0; i < lines.length; i++) {
                if (currentY + lineHeight > pageHeight - bottomMargin) {
                  pdf.addPage();
                  currentY = margin;
                }
                pdf.text(lines[i], x, currentY);
                currentY += lineHeight;
              }

              return currentY;
            };

            // Generate PDF content (simplified version of the full generatePDF function)
            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'bold');
            pdf.text(conversationTitle, margin, yPosition);
            yPosition += 20;

            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(128, 128, 128);
            pdf.text(`Exported on ${new Date().toLocaleString()}`, margin, yPosition);
            yPosition += 8;
            pdf.text('Generated on https://gpt-muse.com', margin, yPosition);
            yPosition += 15;

            pdf.setTextColor(0, 0, 0);

            for (const [index, message] of messages.entries()) {
              const estimatedSpace = 50;
              checkNewPage(estimatedSpace);

              pdf.setFontSize(12);
              pdf.setFont('helvetica', 'bold');
              const roleLabel =
                message.role === 'user' ? 'USER' : getAssistantLabel().toUpperCase();
              pdf.text(roleLabel, margin, yPosition);
              yPosition += 6;

              pdf.setFontSize(8);
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor(128, 128, 128);
              pdf.text(message.timestamp.toLocaleString(), margin, yPosition);
              yPosition += 10;

              pdf.setTextColor(0, 0, 0);
              pdf.line(margin, yPosition, pageWidth - margin, yPosition);
              yPosition += 8;

              if (message.reasoning) {
                checkNewPage(20);
                pdf.setFontSize(9);
                pdf.setTextColor(128, 128, 128);
                pdf.text('Reasoning:', margin, yPosition);
                yPosition += 6;
                yPosition = addFormattedTextWithPageBreaks(
                  message.reasoning,
                  margin,
                  yPosition,
                  9,
                  contentWidth,
                  [128, 128, 128]
                );
                yPosition += 8;
                pdf.setTextColor(0, 0, 0);
              }

              pdf.setFontSize(10);
              pdf.setFont('helvetica', 'normal');
              yPosition = addFormattedTextWithPageBreaks(
                message.content,
                margin,
                yPosition,
                10,
                contentWidth
              );
              yPosition += 8;

              if (includeImages && message.images && message.images.length > 0) {
                for (const image of message.images) {
                  try {
                    yPosition += 5;
                    checkNewPage(100);
                    const imgWidth = Math.min(contentWidth, 120);
                    pdf.addImage(image.url, 'PNG', margin, yPosition, imgWidth, 0);
                    yPosition += 85;
                    yPosition += 10;
                  } catch (error) {
                    console.warn('Failed to add image to PDF:', error);
                    pdf.setFontSize(9);
                    pdf.setTextColor(128, 128, 128);
                    pdf.text(`[Image failed to load]`, margin, yPosition);
                    yPosition += 10;
                    pdf.setTextColor(0, 0, 0);
                  }
                }
              }

              yPosition += 10;

              if (index < messages.length - 1) {
                checkNewPage(20);
                pdf.setDrawColor(200, 200, 200);
                pdf.line(margin, yPosition, pageWidth - margin, yPosition);
                pdf.setDrawColor(0, 0, 0);
                yPosition += 15;
              }
            }

            // Add page numbers
            const pageCount = pdf.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
              pdf.setPage(i);
              pdf.setFontSize(8);
              pdf.setTextColor(128, 128, 128);
              pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 30, pageHeight - 10);
            }

            // Convert to blob and share
            const pdfBlob = pdf.output('blob');
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const file = new File([pdfBlob], `chat-${timestamp}.pdf`, { type: 'application/pdf' });

            await navigator.share({
              title: `${conversationTitle} - Chat Export`,
              text: 'Shared chat conversation from GPT Muse',
              files: [file],
            });

            notifications.show({
              title: 'PDF Shared',
              message: 'Chat PDF has been shared successfully',
              color: 'green',
              icon: <IconCheck size={16} />,
              autoClose: 3000,
            });
          } else {
            // For text formats, share the text content directly
            let content: string;
            let title: string;

            switch (format) {
              case 'plaintext':
                content = formatAsPlaintext(messages, includeImages);
                title = `${conversationTitle} - Plain Text`;
                break;
              case 'markdown':
                content = formatAsMarkdown(messages, includeImages);
                title = `${conversationTitle} - Markdown`;
                break;
              case 'json':
                content = formatAsJSON(messages, includeImages);
                title = `${conversationTitle} - JSON Data`;
                break;
            }

            await navigator.share({
              title,
              text: content,
            });

            notifications.show({
              title: 'Content Shared',
              message: `Chat shared as ${format} successfully`,
              color: 'green',
              icon: <IconCheck size={16} />,
              autoClose: 3000,
            });
          }
        } catch (error) {
          // Handle user cancellation or sharing errors
          if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Sharing failed:', error);
            notifications.show({
              title: 'Sharing Failed',
              message: 'Failed to share content. Please try Copy or Download instead.',
              color: 'red',
              autoClose: 5000,
            });
          }
        }
      },
      [
        canShare,
        messages,
        conversationTitle,
        getAssistantLabel,
        formatAsPlaintext,
        formatAsMarkdown,
        formatAsJSON,
        includeImages,
      ]
    );

    const handleDownload = useCallback(
      (format: 'plaintext' | 'markdown' | 'json' | 'pdf') => {
        if (messages.length === 0) {
          notifications.show({
            title: 'No Messages',
            message: 'There are no messages to export',
            color: 'orange',
            autoClose: 3000,
          });
          return;
        }

        if (format === 'pdf') {
          generatePDF(messages, includeImages);
          return;
        }

        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        let content: string;
        let filename: string;
        let mimeType: string = 'text/plain';

        switch (format) {
          case 'plaintext':
            content = formatAsPlaintext(messages, includeImages);
            filename = `chat-${timestamp}.txt`;
            break;
          case 'markdown':
            content = formatAsMarkdown(messages, includeImages);
            filename = `chat-${timestamp}.md`;
            mimeType = 'text/markdown';
            break;
          case 'json':
            content = formatAsJSON(messages, includeImages);
            filename = `chat-${timestamp}.json`;
            mimeType = 'application/json';
            break;
        }

        downloadFile(content, filename, mimeType);
      },
      [
        messages,
        formatAsPlaintext,
        formatAsMarkdown,
        formatAsJSON,
        downloadFile,
        generatePDF,
        includeImages,
      ]
    );

    const handleCopy = useCallback(
      (format: 'plaintext' | 'markdown' | 'json') => {
        if (messages.length === 0) {
          notifications.show({
            title: 'No Messages',
            message: 'There are no messages to copy',
            color: 'orange',
            autoClose: 3000,
          });
          return;
        }

        let content: string;

        switch (format) {
          case 'plaintext':
            content = formatAsPlaintext(messages, includeImages);
            break;
          case 'markdown':
            content = formatAsMarkdown(messages, includeImages);
            break;
          case 'json':
            content = formatAsJSON(messages, includeImages);
            break;
        }

        copyToClipboard(content, format);
      },
      [messages, formatAsPlaintext, formatAsMarkdown, formatAsJSON, copyToClipboard, includeImages]
    );

    const hasMessages = messages.length > 0;

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Group gap="xs">
            <IconDownload size={20} />
            <Text fw={600}>Save Chat</Text>
          </Group>
        }
        size="xl"
        centered
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            Export your chat conversation in different formats.
            {!hasMessages && ' No messages to export yet.'}
            {hasMessages &&
              ` ${messages.length} message${messages.length === 1 ? '' : 's'} to export.`}
          </Text>

          {hasImages && (
            <Group gap="xs">
              <Checkbox
                checked={includeImages}
                onChange={(event) => setIncludeImages(event.currentTarget.checked)}
                label={
                  <Group gap={4}>
                    <IconPhoto size={14} />
                    <Text size="sm">Include images in export</Text>
                  </Group>
                }
              />
              <Text size="xs" c="dimmed" style={{ marginLeft: '20px' }}>
                (PDF format is best for preserving images)
              </Text>
            </Group>
          )}

          <Grid>
            {/* Plaintext */}
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Paper p="md" withBorder h="100%">
                <Stack gap="sm" align="center">
                  <Text fw={500} size="sm">
                    Plaintext
                  </Text>
                  <Text size="xs" c="dimmed" ta="center">
                    Simple text format with role prefixes
                  </Text>
                  <Stack gap="xs" w="100%">
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconDownload size={14} />}
                      onClick={() => handleDownload('plaintext')}
                      disabled={!hasMessages}
                      fullWidth
                    >
                      Download
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      leftSection={<IconCopy size={14} />}
                      onClick={() => handleCopy('plaintext')}
                      disabled={!hasMessages}
                      fullWidth
                    >
                      Copy
                    </Button>
                    {canShare && (
                      <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconShare size={14} />}
                        onClick={() => shareContent('plaintext')}
                        disabled={!hasMessages}
                        fullWidth
                      >
                        Share
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            </Grid.Col>

            {/* Markdown */}
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Paper p="md" withBorder h="100%">
                <Stack gap="sm" align="center">
                  <Text fw={500} size="sm">
                    Markdown
                  </Text>
                  <Text size="xs" c="dimmed" ta="center">
                    Formatted with headers and preserved structure
                  </Text>
                  <Stack gap="xs" w="100%">
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconDownload size={14} />}
                      onClick={() => handleDownload('markdown')}
                      disabled={!hasMessages}
                      fullWidth
                    >
                      Download
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      leftSection={<IconCopy size={14} />}
                      onClick={() => handleCopy('markdown')}
                      disabled={!hasMessages}
                      fullWidth
                    >
                      Copy
                    </Button>
                    {canShare && (
                      <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconShare size={14} />}
                        onClick={() => shareContent('markdown')}
                        disabled={!hasMessages}
                        fullWidth
                      >
                        Share
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            </Grid.Col>

            {/* JSON */}
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Paper p="md" withBorder h="100%">
                <Stack gap="sm" align="center">
                  <Text fw={500} size="sm">
                    JSON
                  </Text>
                  <Text size="xs" c="dimmed" ta="center">
                    Complete data with metadata and timestamps
                  </Text>
                  <Stack gap="xs" w="100%">
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconDownload size={14} />}
                      onClick={() => handleDownload('json')}
                      disabled={!hasMessages}
                      fullWidth
                    >
                      Download
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      leftSection={<IconCopy size={14} />}
                      onClick={() => handleCopy('json')}
                      disabled={!hasMessages}
                      fullWidth
                    >
                      Copy
                    </Button>
                    {canShare && (
                      <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconShare size={14} />}
                        onClick={() => shareContent('json')}
                        disabled={!hasMessages}
                        fullWidth
                      >
                        Share
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            </Grid.Col>

            {/* PDF */}
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Paper p="md" withBorder h="100%">
                <Stack gap="sm" align="center">
                  <Text fw={500} size="sm">
                    PDF
                  </Text>
                  <Text size="xs" c="dimmed" ta="center">
                    Clean formatted document with text and images
                  </Text>
                  <Stack gap="xs" w="100%">
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconFileTypePdf size={14} />}
                      onClick={() => handleDownload('pdf')}
                      disabled={!hasMessages}
                      fullWidth
                    >
                      Download
                    </Button>
                    {canShare && (
                      <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconShare size={14} />}
                        onClick={() => shareContent('pdf')}
                        disabled={!hasMessages}
                        fullWidth
                      >
                        Share
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            </Grid.Col>
          </Grid>

          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={onClose}>
              Close
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }
);

SaveChatModal.displayName = 'SaveChatModal';
